/**
 * The article editor's list tools (piece 30; T397; decisions 1270, 1271): which
 * list item the caret stands in, what the list bar shows of it, and the kind,
 * start number, indent, outdent and removal the bar presses. 🔨 Each one moves
 * the surface's own nodes and nothing else, so the markup it leaves is what the
 * article's allowed table already keeps: a list, its items, and a numbered
 * list's `start` from 2 up. A list gains no attribute.
 *
 * 💰 A list nested by the browser's own indent command stands straight inside
 * its parent list rather than inside an item, `<ol><li>a</li><ol>…</ol></ol>`.
 * Articles written with the old toolbar buttons hold that shape, so every tool
 * here reads it as the list nested under the item before it, and writes the
 * item-held shape only where it makes a nested list of its own.
 */

/** 🔒 A list nests at most four levels deep (decision 1271). */
export const LIST_MAX_LEVEL = 4;
/** 🔒 A numbered list starts at a whole number from 1 to 999 (decision 1270). */
export const LIST_START_MIN = 1;
export const LIST_START_MAX = 999;

export type ListKind = 'bulleted' | 'numbered';

/** The list bar's presses (decisions 1270, 1271). */
export type ListAction = 'bulleted' | 'numbered' | 'indent' | 'outdent' | 'remove';

/** Why Indent cannot act here: the item has none above it, or nesting it would pass the fourth level. */
export type ListRefusal = 'firstItem' | 'maxDepth';

/** What the list bar shows of the item the caret stands in and of its list. */
export interface ListFacts {
  kind: ListKind;
  /** 1 for a list in the words, 2 for one nested inside it, and so on. */
  level: number;
  /** The items the list holds at this level; a nested list's items are its own. */
  items: number;
  /** The item's place among them, from 1. */
  position: number;
  /** The number the list opens at: its `start`, or 1. */
  start: number;
  /** The item a nested list stands under, its kind and its number, or null at the first level. */
  parent: { kind: ListKind; step: number } | null;
  /** The level of the deepest list the item carries under it, or its own level where it carries none. */
  deepest: number;
}

type ListElement = HTMLOListElement | HTMLUListElement;

function isList(node: Node | null | undefined): node is ListElement {
  return node instanceof HTMLElement && (node.tagName === 'OL' || node.tagName === 'UL');
}

function isItem(node: Node | null | undefined): node is HTMLLIElement {
  return node instanceof HTMLElement && node.tagName === 'LI';
}

/** The list item the caret stands in, the innermost one, when it stands in one inside this surface. */
export function itemAt(surface: HTMLElement, node: Node | null): HTMLLIElement | null {
  let at: Node | null = node;
  while (at && at !== surface) {
    if (isItem(at) && isList(at.parentElement)) return at;
    at = at.parentNode;
  }
  return null;
}

/** The list an item stands in. */
export function listOf(item: HTMLLIElement): ListElement {
  return item.parentElement as ListElement;
}

function kindOf(list: ListElement): ListKind {
  return list.tagName === 'OL' ? 'numbered' : 'bulleted';
}

/** How many lists deep a list stands inside the surface, itself counted. */
function levelOf(surface: HTMLElement, list: HTMLElement): number {
  let level = 0;
  let at: HTMLElement | null = list;
  while (at && at !== surface) {
    if (isList(at)) level += 1;
    at = at.parentElement;
  }
  return level;
}

function itemsOf(list: ListElement): HTMLLIElement[] {
  return Array.from(list.children).filter(isItem);
}

function startOf(list: ListElement): number {
  if (list.tagName !== 'OL') return 1;
  const start = Number.parseInt(list.getAttribute('start') ?? '', 10);
  return Number.isInteger(start) && start > 0 ? start : 1;
}

/** An item's number in its list: the list's start and the items before it. */
function stepOf(item: HTMLLIElement): number {
  const list = listOf(item);
  return startOf(list) + itemsOf(list).indexOf(item);
}

/**
 * The lists that stand under an item: those inside it, and those the browser's
 * indent left straight after it in the same list, up to the next item.
 */
function trailingLists(item: HTMLLIElement): ListElement[] {
  const found: ListElement[] = [];
  let at = item.nextElementSibling;
  while (isList(at)) {
    found.push(at);
    at = at.nextElementSibling;
  }
  return found;
}

function nestedLists(item: HTMLLIElement): ListElement[] {
  return [...Array.from(item.children).filter(isList), ...trailingLists(item)];
}

/** The item a nested list stands under: the item holding it, or the item before it in the browser's shape. */
function parentItemOf(list: ListElement): HTMLLIElement | null {
  const holder = list.parentElement;
  if (isItem(holder) && isList(holder.parentElement)) return holder;
  if (!isList(holder)) return null;
  let at = list.previousElementSibling;
  while (at && !isItem(at)) at = at.previousElementSibling;
  return isItem(at) ? at : null;
}

/** How many lists deep the deepest list inside this one stands below it, this one counted. */
function depthBelow(list: ListElement): number {
  let deepest = 1;
  for (const inner of Array.from(list.querySelectorAll('ol, ul'))) {
    let depth = 1;
    let at: HTMLElement | null = inner as HTMLElement;
    while (at && at !== list) {
      if (isList(at)) depth += 1;
      at = at.parentElement;
    }
    deepest = Math.max(deepest, depth);
  }
  return deepest;
}

/** What the list bar shows of the item the caret stands in (decision 1270). */
export function listFactsOf(surface: HTMLElement, item: HTMLLIElement): ListFacts {
  const list = listOf(item);
  const items = itemsOf(list);
  const level = levelOf(surface, list);
  const parentItem = level > 1 ? parentItemOf(list) : null;
  const under = nestedLists(item).map(depthBelow);
  return {
    kind: kindOf(list),
    level,
    items: items.length,
    position: items.indexOf(item) + 1,
    start: startOf(list),
    parent: parentItem ? { kind: kindOf(listOf(parentItem)), step: stepOf(parentItem) } : null,
    deepest: level + (under.length > 0 ? Math.max(...under) : 0),
  };
}

/**
 * 🔒 Why Indent cannot act (decision 1271): the list's first item has no item
 * above it to nest under, and no list nests past the fourth level, counting the
 * lists the item carries under it.
 */
export function listRefusal(facts: ListFacts): ListRefusal | null {
  if (facts.position <= 1) return 'firstItem';
  if (facts.deepest + 1 > LIST_MAX_LEVEL) return 'maxDepth';
  return null;
}

/** A typed start number read as the list keeps it, or null for anything else: a fraction, a word, a bound crossed. */
export function listStartOf(typed: string): number | null {
  const trimmed = typed.trim();
  if (!/^\d{1,3}$/.test(trimmed)) return null;
  const start = Number(trimmed);
  return start >= LIST_START_MIN && start <= LIST_START_MAX ? start : null;
}

/**
 * 🔒 The number the numbered list before this one ends on (T2802): the nearest numbered list standing before it
 * among its siblings, its start plus its items less one, or null where there is none. Continue Previous List
 * starts this list one past it.
 */
export function previousListEnd(list: ListElement): number | null {
  let at = list.previousElementSibling;
  while (at) {
    if (at.tagName === 'OL') {
      const start = Number((at as HTMLOListElement).getAttribute('start'));
      const first = Number.isInteger(start) && start >= 1 ? start : 1;
      return first + Array.from(at.children).filter((child) => child.tagName === 'LI').length - 1;
    }
    at = at.previousElementSibling;
  }
  return null;
}

/**
 * 🔒 Bulleted or Numbered for the whole list at the caret's level (decision
 * 1270): the list's items move into a list of the other kind, and a nested list
 * keeps its own. A bulleted list keeps no start number and no letters, so those
 * go with the change. The list the items now stand in is returned.
 */
export function setListKind(list: ListElement, kind: ListKind): ListElement {
  if (kindOf(list) === kind) return list;
  const next = document.createElement(kind === 'numbered' ? 'ol' : 'ul');
  while (list.firstChild) next.appendChild(list.firstChild);
  list.replaceWith(next);
  return next;
}

/** 🔒 Start At on a numbered list (decision 1270): 1 writes nothing, as a list with no start already opens at 1. */
export function setListStart(list: ListElement, start: number): void {
  if (list.tagName !== 'OL') return;
  if (start <= 1) list.removeAttribute('start');
  else list.setAttribute('start', String(start));
}

/**
 * 🔒 Indent nests the item under the one above it (decision 1271), in that
 * item's nested list where it already has one at its end, or in a new list of
 * the same kind; the lists the item carries move with it. It returns false,
 * changing nothing, where the list bar greys it.
 */
export function indentItem(surface: HTMLElement, item: HTMLLIElement): boolean {
  if (listRefusal(listFactsOf(surface, item)) !== null) return false;
  const list = listOf(item);
  const moving = [item, ...trailingLists(item)];
  const before = item.previousElementSibling;
  let target: ListElement;
  if (isList(before)) {
    target = before;
  } else {
    const above = before as HTMLLIElement;
    const last = above.lastElementChild;
    if (isList(last)) {
      target = last;
    } else {
      target = document.createElement(list.tagName === 'OL' ? 'ol' : 'ul');
      above.appendChild(target);
    }
  }
  for (const node of moving) target.appendChild(node);
  return true;
}

/** The blocks that stand directly in the words, a line of their own each. */
const BLOCKS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'PRE', 'BLOCKQUOTE', 'TABLE', 'HR']);

function holdsAnything(node: Node): boolean {
  if ((node.textContent ?? '').trim() !== '') return true;
  return node instanceof Element ? node.matches('img, table, hr, br') || node.querySelector('img, table, hr, br') !== null : false;
}

/**
 * An item's words as the blocks they stand in once they leave the list: a run
 * of words a paragraph, a block such as a table kept whole, and the lists it
 * carries apart. An item with no words at all becomes one empty paragraph, so
 * the caret still has a line to stand on.
 */
function unwrapItem(item: HTMLLIElement): { blocks: HTMLElement[]; line: HTMLElement; lists: ListElement[] } {
  const blocks: HTMLElement[] = [];
  const lists: ListElement[] = [];
  let run: HTMLParagraphElement | null = null;
  for (const node of Array.from(item.childNodes)) {
    if (isList(node)) {
      lists.push(node);
      run = null;
    } else if (node instanceof HTMLElement && BLOCKS.has(node.tagName)) {
      blocks.push(node);
      run = null;
    } else if (run || holdsAnything(node)) {
      if (!run) {
        run = document.createElement('p');
        blocks.push(run);
      }
      run.appendChild(node);
    }
  }
  if (blocks.length === 0) {
    const empty = document.createElement('p');
    empty.innerHTML = '<br>';
    blocks.push(empty);
  }
  return { blocks, line: blocks[0]!, lists };
}

/**
 * 🔒 Outdent brings the item back a level (decision 1271). Nested, it stands
 * next after the item it was nested under, and the items that followed it in
 * its nested list nest under it now. At the first level it leaves the list as a
 * paragraph, and the items after it stay a list numbered on from the ones before.
 * A nested list goes when its last item has left. The node the caret belongs in
 * is returned: the item, or the paragraph it became.
 */
export function outdentItem(surface: HTMLElement, item: HTMLLIElement): HTMLElement {
  const list = listOf(item);
  const carried = trailingLists(item);
  const following: Element[] = [];
  let at = (carried[carried.length - 1] ?? item).nextElementSibling;
  while (at) {
    following.push(at);
    at = at.nextElementSibling;
  }
  const holder = list.parentElement;
  if (levelOf(surface, list) === 1 || (!isItem(holder) && !isList(holder))) {
    const step = stepOf(item);
    const { blocks, line, lists } = unwrapItem(item);
    let rest: ListElement | null = null;
    if (following.length > 0) {
      rest = list.cloneNode(false) as ListElement;
      for (const node of following) rest.appendChild(node);
      // The items after it number on from the items before it, so the steps still count without a gap.
      if (rest.tagName === 'OL') setListStart(rest, step);
    }
    list.after(...blocks, ...lists, ...carried, ...(rest ? [rest] : []));
    item.remove();
    if (list.children.length === 0) list.remove();
    return line;
  }
  let rest: ListElement | null = null;
  if (following.length > 0) {
    rest = list.cloneNode(false) as ListElement;
    rest.removeAttribute('start');
    for (const node of following) rest.appendChild(node);
  }
  if (isItem(holder)) {
    // The item-held shape: it follows the item it was nested under, and what followed it nests inside it.
    const anchor = [holder, ...trailingLists(holder)].pop()!;
    anchor.after(item, ...carried);
    if (rest) {
      if (carried.length > 0) carried[carried.length - 1]!.after(rest);
      else item.appendChild(rest);
    }
  } else {
    // The browser's shape: the nested list splits round the item, which stands after it one level up.
    list.after(item, ...carried, ...(rest ? [rest] : []));
  }
  if (list.children.length === 0) list.remove();
  return item;
}

/** The list the item stands in at the first level: the one Remove List turns into paragraphs. */
function outermostList(surface: HTMLElement, item: HTMLLIElement): ListElement {
  let top: ListElement = listOf(item);
  let at: HTMLElement | null = top.parentElement;
  while (at && at !== surface) {
    if (isList(at)) top = at;
    at = at.parentElement;
  }
  return top;
}

/**
 * 🔒 Remove List turns the whole list the caret stands in back into paragraphs
 * (decision 1270), its nested lists with it, in reading order, every word
 * kept. The paragraph the caret's item became is returned.
 */
export function removeList(surface: HTMLElement, item: HTMLLIElement): HTMLElement {
  const top = outermostList(surface, item);
  let caretLine: HTMLElement | null = null;
  const flatten = (list: ListElement): HTMLElement[] => {
    const out: HTMLElement[] = [];
    for (const child of Array.from(list.children)) {
      if (isList(child)) {
        out.push(...flatten(child));
      } else if (isItem(child)) {
        const { blocks, line, lists } = unwrapItem(child);
        if (child === item) caretLine = line;
        out.push(...blocks);
        for (const inner of lists) out.push(...flatten(inner));
      }
    }
    return out;
  };
  const blocks = flatten(top);
  top.replaceWith(...blocks);
  return caretLine ?? blocks[0] ?? surface;
}

/** An item with nothing in it: no word, no picture, no list under it. Enter there leaves the list (decision 1272). */
export function isEmptyItem(item: HTMLLIElement): boolean {
  return (item.textContent ?? '').trim() === '' && item.querySelector('img, table, hr, ol, ul') === null && trailingLists(item).length === 0;
}
